import { Fragment, useEffect, useState } from 'react'
import { Combobox, Transition } from '@headlessui/react'
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid'
import useDebounce from 'utils/useDebounceHook'
import { useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { Client, ClientDTO, mapClientFromDTO } from 'types/client'
export interface ClientComboboxUIProps {
    tenant_id: string
    onSelect(client?: Client): void
    searchType?: string
    disable?: boolean
}

export function ClientCombobox({
    tenant_id,
    onSelect,
    searchType = 'clients',
    disable = false,
}: ClientComboboxUIProps) {
    const [people, setPeople] = useState<Client[]>([])
    const [selected, setSelected] = useState<Client>()
    const [query, setQuery] = useState('')
    const debouncedSearchTerm = useDebounce(query, 200)

    const xhr = useFetch(
        `/v1/${searchType}/search?tenantId=${tenant_id}&searchStr=${query}&size=50&sort=u.last_name`,
        FetchMethods.GET,
        false
    )

    function handleSelection(selection: Client) {
        setSelected(selection)
        onSelect(selection)
    }

    useEffect(() => {
        console.log(query, "queryquery");
        
        if (query.length > 1) {
            xhr.doFetch()
        }
    }, [debouncedSearchTerm])

    useEffect(() => {
        if (!xhr.is_loading && xhr.data.content?.length > 0) {
            const clients = xhr.data.content.map((c: ClientDTO) => {
                return mapClientFromDTO(c)
            })
            setPeople(clients)
        }
    }, [xhr.is_loading])

    return (
        <Combobox
            value={selected}
            onChange={handleSelection}
            disabled={disable}
        >
            <div className={`relative `}>
                <div className="relative w-full text-left bg-white cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-primary-light focus-visible:ring-offset-2 sm:text-sm overflow-hidden">
                    <Combobox.Input
                        className="w-full border-none focus:ring-0 py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 aot-input"
                        displayValue={(person: Client) =>
                            person
                                ? `${person.user.first_name} ${person.user.last_name}`
                                : ''
                        }
                        onChange={(event) => {
                            if (selected) {
                                if (
                                    event.target.value !==
                                    [
                                        selected.user.first_name,
                                        selected.user.last_name,
                                    ].join(' ')
                                ) {
                                    setSelected(undefined)
                                    onSelect(undefined)
                                }
                            }
                            setQuery(event.target.value)
                        }}
                    />
                    <Combobox.Button
                        className="absolute inset-y-0 right-0 flex items-center pr-2"
                        placeholder="Select from existing clients"
                    >
                        <SelectorIcon
                            className="w-5 h-5 text-gray-400"
                            aria-hidden="true"
                        />
                    </Combobox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <Combobox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                        {people.length === 0 && query !== '' ? (
                            <div className="cursor-default select-none relative py-2 px-4 text-gray-700">
                                Nothing found.
                            </div>
                        ) : (
                            people.map((person) => (
                                <Combobox.Option
                                    key={person.id}
                                    className={({ active }) =>
                                        `cursor-default select-none relative py-2 pl-10 pr-4 ${
                                            active
                                                ? 'text-white bg-primary-light'
                                                : 'text-gray-900'
                                        }`
                                    }
                                    value={person}
                                >
                                    {({ selected, active }) => (
                                        <>
                                            <span
                                                className={`block truncate ${
                                                    selected
                                                        ? 'font-medium'
                                                        : 'font-normal'
                                                }`}
                                            >
                                                {person.user.first_name}{' '}
                                                {person.user.last_name}
                                            </span>
                                            {selected ? (
                                                <span
                                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                        active
                                                            ? 'text-white'
                                                            : 'text-teal-600'
                                                    }`}
                                                >
                                                    <CheckIcon
                                                        className="w-5 h-5"
                                                        aria-hidden="true"
                                                    />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </Combobox.Option>
                            ))
                        )}
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    )
}
